import React, { useState, useEffect } from 'react';

interface Skill {
  id: number;
  name: string;
}

interface UserProfileSkill {
  id: number;
  skill_id: number;
  years_of_experience: number;
  skill: Skill;
}

interface UserProfileSkillsPageProps {
  initialSkills?: UserProfileSkill[];
  allSkills?: Skill[];
  isUserSignedIn?: boolean;
}

const UserProfileSkillsPage: React.FC<UserProfileSkillsPageProps> = ({
  initialSkills = [],
  allSkills = [],
  isUserSignedIn = false
}) => {
  const [userProfileSkills, setUserProfileSkills] = useState<UserProfileSkill[]>(initialSkills);
  const [allSkillsList, setAllSkillsList] = useState<Skill[]>(allSkills);
  const [isLoading, setIsLoading] = useState(false);
  const [newSkillId, setNewSkillId] = useState<number | ''>('');
  const [newYearsOfExperience, setNewYearsOfExperience] = useState<number>(1);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingSkillId, setEditingSkillId] = useState<number | ''>('');
  const [editingYears, setEditingYears] = useState<number>(1);

  // コンポーネントマウント時にデータを取得
  useEffect(() => {
    if (isUserSignedIn) {
      fetchUserProfileSkills();
      fetchAllSkills();
    }
  }, [isUserSignedIn]);

  // スキル一覧を取得
  const fetchUserProfileSkills = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/mypage/user_profile_skills.json', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfileSkills(data.user_profile_skills || []);
      } else {
        console.error('Failed to fetch user profile skills:', response.status);
      }
    } catch (error) {
      console.error('Error fetching user profile skills:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // 全スキル一覧を取得
  const fetchAllSkills = async () => {
    try {
      const response = await fetch('/api/v1/skills', {
        headers: {
          'Accept': 'application/json'
        }
      });

      if (response.ok) {
        const groupedData = await response.json();
        const flatSkills: Skill[] = [];
        groupedData.forEach((group: any) => {
          if (group.skills && Array.isArray(group.skills)) {
            flatSkills.push(...group.skills);
          }
        });
        setAllSkillsList(flatSkills);
      }
    } catch (error) {
      console.error('Error fetching all skills:', error);
    }
  };

  // スキルを追加
  const handleCreate = async () => {
    if (!newSkillId || newSkillId === '') return;

    setIsLoading(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch('/mypage/user_profile_skills', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({
          user_profile_skill: {
            skill_id: newSkillId,
            years_of_experience: newYearsOfExperience
          }
        })
      });

      if (response.ok) {
        await fetchUserProfileSkills();
        setNewSkillId('');
        setNewYearsOfExperience(1);
        if (window.showGlobalFlash) {
          window.showGlobalFlash('スキルを追加しました', 'success', false);
        }
      } else {
        const errorData = await response.json();
        if (window.showGlobalFlash) {
          window.showGlobalFlash(errorData.errors?.join(', ') || 'スキルの追加に失敗しました', 'error', false);
        }
      }
    } catch (error) {
      if (window.showGlobalFlash) {
        window.showGlobalFlash('ネットワークエラーが発生しました', 'error', false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // スキルを更新
  const handleUpdate = async (id: number) => {
    if (!editingSkillId || editingSkillId === '') return;

    setIsLoading(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch(`/mypage/user_profile_skills/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        },
        body: JSON.stringify({
          user_profile_skill: {
            skill_id: editingSkillId,
            years_of_experience: editingYears
          }
        })
      });

      if (response.ok) {
        await fetchUserProfileSkills();
        setEditingId(null);
        if (window.showGlobalFlash) {
          window.showGlobalFlash('スキルを更新しました', 'success', false);
        }
      } else {
        const errorData = await response.json();
        if (window.showGlobalFlash) {
          window.showGlobalFlash(errorData.errors?.join(', ') || 'スキルの更新に失敗しました', 'error', false);
        }
      }
    } catch (error) {
      if (window.showGlobalFlash) {
        window.showGlobalFlash('ネットワークエラーが発生しました', 'error', false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // スキルを削除
  const handleDelete = async (id: number) => {
    if (!confirm('本当に削除しますか？')) return;

    setIsLoading(true);
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
      const response = await fetch(`/mypage/user_profile_skills/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken || ''
        }
      });

      if (response.ok) {
        await fetchUserProfileSkills();
        if (window.showGlobalFlash) {
          window.showGlobalFlash('スキルを削除しました', 'success', false);
        }
      } else {
        if (window.showGlobalFlash) {
          window.showGlobalFlash('スキルの削除に失敗しました', 'error', false);
        }
      }
    } catch (error) {
      if (window.showGlobalFlash) {
        window.showGlobalFlash('ネットワークエラーが発生しました', 'error', false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // 編集開始
  const startEditing = (skill: UserProfileSkill) => {
    setEditingId(skill.id);
    setEditingSkillId(skill.skill_id);
    setEditingYears(skill.years_of_experience);
  };

  // 編集キャンセル
  const cancelEditing = () => {
    setEditingId(null);
    setEditingSkillId('');
    setEditingYears(1);
  };

  // ログインしていない場合の表示
  if (!isUserSignedIn) {
    return (
      <div className="container p-4">
        <div className="max-w-lg mx-auto text-center">
          <h1 className="h3 fw-bold mb-4">スキル管理</h1>
          <p className="text-muted mb-4">
            新規アカウントを登録すると、自分に合ったスキルや職種の案件を選択できます。
            自分に合った案件を見つけることができます。
          </p>
          <a href="/users/sign_up" className="btn btn-primary me-2">
            無料登録
          </a>
          <a href="/users/sign_in" className="btn btn-outline-primary">
            ログイン
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="container p-4">
      <h2 className="mb-4">スキル管理</h2>

      {/* 新規追加フォーム */}
      <div className="card mb-4">
        <div className="card-header">新しいスキルを追加</div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">スキル</label>
            <select
              className="form-select"
              value={newSkillId}
              onChange={(e) => setNewSkillId(e.target.value ? parseInt(e.target.value) : '')}
            >
              <option value="">選択してください</option>
              {allSkillsList.map((skill) => (
                <option key={skill.id} value={skill.id}>
                  {skill.name}
                </option>
              ))}
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">経験年数</label>
            <input
              type="number"
              className="form-control"
              min="0"
              value={newYearsOfExperience}
              onChange={(e) => setNewYearsOfExperience(parseInt(e.target.value) || 0)}
            />
          </div>
          <button
            className="btn btn-primary"
            onClick={handleCreate}
            disabled={isLoading || !newSkillId}
          >
            スキルを追加
          </button>
        </div>
      </div>

      {/* 既存スキル一覧 */}
      <table className="table table-bordered align-middle">
        <thead>
          <tr>
            <th>スキル</th>
            <th>経験年数</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {userProfileSkills.map((ups) => (
            <tr key={ups.id}>
              <td>
                {editingId === ups.id ? (
                  <select
                    className="form-select d-inline-block w-auto"
                    value={editingSkillId}
                    onChange={(e) => setEditingSkillId(e.target.value ? parseInt(e.target.value) : '')}
                  >
                    {allSkillsList.map((skill) => (
                      <option key={skill.id} value={skill.id}>
                        {skill.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  ups.skill.name
                )}
              </td>
              <td>
                {editingId === ups.id ? (
                  <input
                    type="number"
                    className="form-control d-inline-block w-auto"
                    min="0"
                    value={editingYears}
                    onChange={(e) => setEditingYears(parseInt(e.target.value) || 0)}
                  />
                ) : (
                  ups.years_of_experience
                )}
              </td>
              <td className="text-end">
                {editingId === ups.id ? (
                  <>
                    <button
                      className="btn btn-sm btn-primary me-2"
                      onClick={() => handleUpdate(ups.id)}
                      disabled={isLoading}
                    >
                      更新する
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={cancelEditing}
                    >
                      キャンセル
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="btn btn-sm btn-primary me-2"
                      onClick={() => startEditing(ups)}
                    >
                      編集
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(ups.id)}
                      disabled={isLoading}
                    >
                      削除する
                    </button>
                  </>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default UserProfileSkillsPage;

